import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import LanguageToggle from "./components/LanguageToggle";
import HeroSection from "./components/HeroSection";
import WhatIsKundliSection from "./components/WhatIsKundliSection";
import ZodiacWheelSection from "./components/ZodiacWheelSection";
import WhoShouldGetSection from "./components/WhoShouldGetSection";
import WhyShouldYouSection from "./components/WhyShouldYouSection";
import ProblemSection from "./components/ProblemSection";
import ImageGallerySection from "./components/ImageGallerySection";
import FeaturesSection from "./components/FeaturesSection";
import BeforeAfterSection from "./components/BeforeAfterSection";
import TestimonialsSection from "./components/TestimonialsSection";
import AboutAstrologerSection from "./components/AboutAstrologerSection";
import BonusSection from "./components/BonusSection";
import PricingSection from "./components/PricingSection";
import FAQSection from "./components/FAQSection";
import CTASection from "./components/CTASection";
import Reveal from "./components/premium/Reveal";
import ScrollProgressBar from "./components/premium/ScrollProgressBar";
import StickyCTA from "./components/premium/StickyCTA";
import SplashScreen from "./components/premium/SplashScreen";
import Footer from "./components/Footer";
import IntakeFormModal from "./components/IntakeFormModal";
import InAppBrowserWarning from "./components/InAppBrowserWarning";
import SuccessPage from "./pages/SuccessPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AdminOrdersPage from "./pages/AdminOrdersPage";
import AdminPage from "./pages/AdminPage";
import BraceletCheckoutPage, { BraceletSuccessPage } from "./pages/BraceletCheckoutPage";
import ConsultationCheckoutPage, { ConsultationSuccessPage } from "./pages/ConsultationCheckoutPage";
import RedoKundaliPage from "./pages/RedoKundaliPage";
import UnsubscribePage from "./pages/UnsubscribePage";

// Meta Pixel ID
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// Initialize Meta Pixel
const initMetaPixel = () => {
  if (window.fbq) return;
  
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  if (META_PIXEL_ID) {
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    // Engagement signal — improves Pixel-only attribution since we don't have
    // a server-side CAPI fallback anymore. Fires once per page load.
    window.fbq('track', 'ViewContent', {
      content_name: 'Karma Kundali Landing',
      content_category: 'Astrology',
      content_type: 'product',
      value: 99,
      currency: 'INR',
    });
  }
};

// Tiny helper so other components don't have to remember the eventID convention.
export const fireMetaEvent = (event, params, eventID) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', event, params || {}, eventID ? { eventID } : undefined);
    }
  } catch (fbqErr) {
    console.error('[fbq] track failed:', fbqErr);
  }
};

const LandingPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpenForm = () => {
    setIsFormOpen(true);
    // Track InitiateCheckout when form opens — this is our strongest
    // top-of-funnel intent signal in pixel-only mode.
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Karma Kundali Premium Report',
        content_ids: ['kundali-premium'],
        content_type: 'product',
        value: 99,
        currency: 'INR',
        num_items: 1,
      });
      window.fbq('track', 'Lead', { value: 99, currency: 'INR' });
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  const handlePaymentSuccess = (orderId) => {
    setIsFormOpen(false);
    navigate(`/success?order_id=${orderId}`);
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-brand-cream">
      <SplashScreen />
      <ScrollProgressBar />
      <LanguageToggle />
      <StickyCTA onOpenForm={handleOpenForm} />

      {/* 1. Hero — keeps its own intro animations */}
      <HeroSection onOpenForm={handleOpenForm} />

      {/* 2. What is Premium Kundli Report? */}
      <Reveal variant="up"><WhatIsKundliSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 3. Vedic Zodiac Wheel — moving rashi chakra */}
      <Reveal variant="scale" duration={900}><ZodiacWheelSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 4. Who Should Get Janam Kundli — 5 personas */}
      <Reveal variant="up"><WhoShouldGetSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 5. Why Should You Get — 3 benefit cards */}
      <Reveal variant="up"><WhyShouldYouSection /></Reveal>

      {/* 6. Problems we solve */}
      <Reveal variant="left"><ProblemSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 7. What's inside the report — 5 feature blocks */}
      <Reveal variant="right"><FeaturesSection /></Reveal>

      {/* 8. Quick visual preview */}
      <Reveal variant="up"><ImageGallerySection /></Reveal>

      {/* 9. Before / After comparison */}
      <Reveal variant="scale" duration={900}><BeforeAfterSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 10. About Karma Kundali astrologer + Featured In */}
      <Reveal variant="up"><AboutAstrologerSection /></Reveal>

      {/* 11. Pricing — comparison table + premium card */}
      <Reveal variant="up"><PricingSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 12. Bonus — free numerology */}
      <Reveal variant="left"><BonusSection onOpenForm={handleOpenForm} /></Reveal>

      {/* 13. Testimonials with photos */}
      <Reveal variant="up"><TestimonialsSection /></Reveal>

      {/* 14. FAQ */}
      <Reveal variant="up"><FAQSection /></Reveal>

      {/* 15. Final CTA */}
      <Reveal variant="scale" duration={900}><CTASection onOpenForm={handleOpenForm} /></Reveal>

      {/* 16. Footer */}
      <Footer />

      <IntakeFormModal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

const InAppBrowserWarningContainer = () => {
  const { language } = useLanguage();
  return <InAppBrowserWarning language={language} />;
};

function App() {
  useEffect(() => {
    // Initialize Meta Pixel on app load
    initMetaPixel();
  }, []);

  return (
    <LanguageProvider>
      <div className="App">
        <InAppBrowserWarningContainer />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsConditionsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/admin" element={<AdminOrdersPage />} />
            <Route path="/admin/console" element={<AdminPage />} />
            <Route path="/bracelet" element={<BraceletCheckoutPage />} />
            <Route path="/bracelet/success" element={<BraceletSuccessPage />} />
            <Route path="/consultation" element={<ConsultationCheckoutPage />} />
            <Route path="/consultation/success" element={<ConsultationSuccessPage />} />
            <Route path="/redo" element={<RedoKundaliPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#1A0B0B',
              color: '#FFF8E1',
              border: '1px solid rgba(255, 215, 0, 0.2)',
            },
          }}
        />
      </div>
    </LanguageProvider>
  );
}

export default App;
